import { FormEvent, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { aiAssistantApi, type AssistantAnswer, type AssistantMode } from '../lib/api/ai-assistant';

type Message = { id: number; role: 'user' | 'assistant'; text: string; answer?: AssistantAnswer };

const PAGE_SUGGESTIONS: Record<string, string[]> = {
  '/cashledger': ['Tổng chi tiền mặt gần nhất là bao nhiêu?', 'Dự báo chi tiền kỳ tới'],
  '/approvals': ['Có bao nhiêu hồ sơ chờ duyệt?', 'Hồ sơ nào cần xử lý trước?'],
  '/payroll': ['Tóm tắt bảng lương gần nhất', 'Giải thích cách tính lương thực nhận'],
  '/payments': ['Có bao nhiêu hồ sơ chờ duyệt?', 'Tổng chi tiền mặt gần nhất là bao nhiêu?'],
};

const MODE_META: Record<AssistantMode, { label: string; icon: string }> = {
  query: { label: 'Truy vấn', icon: '⌕' },
  explain: { label: 'Giải thích', icon: '◉' },
  forecast: { label: 'Dự báo', icon: '⌁' },
};

function SparkleIcon({ size = 24 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.94 15.5a2 2 0 0 0-1.44-1.44l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.13a.5.5 0 0 1 .96 0l1.58 6.13a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96l-6.13 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z" /></svg>;
}

export function AiAssistant() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const textarea = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AssistantMode>('query');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const suggestions = useMemo(() => PAGE_SUGGESTIONS[pathname] ?? ['Tổng chi tiền mặt gần nhất là bao nhiêu?', 'Có bao nhiêu hồ sơ chờ duyệt?', 'Tóm tắt bảng lương gần nhất', 'Giải thích cách tính lương thực nhận'], [pathname]);

  const ask = async (question?: string) => {
    const text = (question ?? input).trim();
    if (!text || loading) return;
    setError('');
    setInput('');
    setMessages((current) => [...current, { id: Date.now(), role: 'user', text }]);
    setLoading(true);
    try {
      const answer = await aiAssistantApi.ask(text, mode, pathname);
      setMessages((current) => [...current, { id: Date.now() + 1, role: 'assistant', text: answer.answer, answer }]);
    } catch {
      setError('Không thể truy vấn dữ liệu lúc này. Hãy kiểm tra kết nối API và thử lại.');
    } finally {
      setLoading(false);
      window.setTimeout(() => textarea.current?.focus(), 0);
    }
  };

  const submit = (event: FormEvent) => { event.preventDefault(); void ask(); };
  const openAction = (label: string) => {
    if (label.includes('Sổ quỹ')) navigate('/cashledger');
    else if (label.includes('phê duyệt')) navigate('/approvals');
    else if (label.includes('Bảng lương')) navigate('/payroll');
    else if (label.includes('Hướng dẫn')) navigate('/guide');
  };

  return <>
    <button className="ai-fab" type="button" title="Hỏi trợ lý AI" onClick={() => setOpen(true)}><SparkleIcon /></button>
    {open && <aside className="ai-panel" aria-label="Trợ lý AI Kế toán">
      <header className="ai-panel-head">
        <div className="ai-panel-title"><span className="ai-panel-mark"><SparkleIcon size={20} /></span><span><b>Trợ lý AI Kế toán</b><small>Powered by HITEK AI · chỉ đọc dữ liệu</small></span></div>
        <button type="button" title="Đóng" onClick={() => setOpen(false)} className="ai-head-close">×</button>
        <div className="ai-mode-tabs">
          {(Object.keys(MODE_META) as AssistantMode[]).map((key) => <button key={key} type="button" className={mode === key ? 'is-active' : ''} onClick={() => setMode(key)}><span>{MODE_META[key].icon}</span>{MODE_META[key].label}</button>)}
        </div>
      </header>
      <div className="ai-conversation">
        {messages.length === 0 ? <div className="ai-empty">
          <span className="ai-empty-mark"><SparkleIcon size={28} /></span>
          <h3>Bắt đầu bằng câu hỏi</h3><p>Trợ lý AI trả lời dựa trên dữ liệu kế toán của bạn</p>
          <span className="ai-page-hint">Gợi ý theo màn hình hiện tại</span>
          <div className="ai-suggestions">{suggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => void ask(suggestion)}>{suggestion}</button>)}</div>
        </div> : messages.map((message) => {
          if (message.role === 'user') return <div className="ai-user-message" key={message.id}>{message.text}</div>;
          return <div className="ai-answer" key={message.id}>
            <span className="ai-answer-mark"><SparkleIcon size={14} /></span>
            <div>
              <p>{message.text}</p>
              {message.answer && <>
                <div className="ai-answer-source">Nguồn: <b>{message.answer.source}</b><span>Confidence {message.answer.confidence}%</span></div>
                {message.answer.actions.length > 0 && <div className="ai-answer-actions">
                  {message.answer.actions.map((action) => <button type="button" key={action} onClick={() => openAction(action)}>{action} ↗</button>)}
                </div>}
              </>}
            </div>
          </div>;
        })}
        {loading && <div className="ai-answer ai-loading"><span className="ai-answer-mark"><SparkleIcon size={14} /></span><span>Đang phân tích dữ liệu…</span></div>}
        {error && <p className="ai-error">{error}</p>}
      </div>
      <form className="ai-composer" onSubmit={submit}>
        <div><textarea ref={textarea} rows={1} value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void ask(); } }} placeholder="Hỏi bất cứ điều gì về dữ liệu kế toán..." /><button type="submit" disabled={!input.trim() || loading} title="Gửi">↑</button></div>
        <p>AI có thể sai sót. Kiểm tra lại thông tin quan trọng trước khi dùng.</p>
      </form>
    </aside>}
  </>;
}
